import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const removeToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  // Memoized so identities stay stable across renders (prevents effect re-fire loops)
  const value = useMemo(() => ({
    showToast: (msg, type = 'info', dur) => addToast(msg, type, dur),
    success:   (msg, dur) => addToast(msg, 'success', dur),
    error:     (msg, dur) => addToast(msg, 'error', dur),
    warning:   (msg, dur) => addToast(msg, 'warning', dur),
    info:      (msg, dur) => addToast(msg, 'info', dur),
  }), [addToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };

const ToastContainer = ({ toasts, onRemove }) => (
  <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,.16)',
        background: t.type === 'success' ? '#ECFDF5' : t.type === 'error' ? '#FEF2F2' : t.type === 'warning' ? '#FFFBEB' : '#F0F9FF',
        borderLeft: `4px solid ${t.type === 'success' ? '#059669' : t.type === 'error' ? '#DC2626' : t.type === 'warning' ? '#D97706' : '#0284C7'}`,
        animation: 'fadeIn .2s ease', maxWidth: 380, fontFamily: 'var(--font-body)', fontSize: 14
      }}>
        <span style={{ fontSize: 16, fontWeight: 700, flexShrink: 0,
          color: t.type === 'success' ? '#059669' : t.type === 'error' ? '#DC2626' : t.type === 'warning' ? '#D97706' : '#0284C7'
        }}>{icons[t.type]}</span>
        <span style={{ flex: 1, color: '#1e293b', lineHeight: 1.4 }}>{t.message}</span>
        <button onClick={() => onRemove(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
      </div>
    ))}
  </div>
);
